import { useState, useCallback, useRef } from "react";

const PUBMED_BASE = "https://pubmed.ncbi.nlm.nih.gov/";
// NCBI eSummary — CORS-friendly, free, no API key needed
const EUTILS_BASE = "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi";

/** Parse raw comma-separated input into array of numeric ID strings */
export function parsePubmedIds(raw) {
  if (!raw && raw !== 0) return [];
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^\d+$/.test(s));
}

/** Build a clickable PubMed URL for a single ID */
export function pubmedUrl(id) {
  return `${PUBMED_BASE}${id}/`;
}

/**
 * usePubmedValidator
 *
 * Uses NCBI eSummary API (JSON, CORS-friendly, free, no key required).
 * All IDs in a field are batched into ONE request per blur — very efficient.
 *
 * validityMap  — { [fieldName]: { [id]: "valid"|"invalid"|"checking"|"unknown" } }
 * validateIds  — call on blur: validateIds(fieldName, rawValue)
 * resetField   — call when a field is cleared
 * resetAll     — call on full form reset
 */
export function usePubmedValidator() {
  const [validityMap, setValidityMap] = useState({});
  const controllersRef = useRef({});

  const validateIds = useCallback(async (fieldName, rawValue) => {
    const ids = parsePubmedIds(rawValue);

    if (ids.length === 0) {
      setValidityMap((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
      return;
    }

    // Cancel any previous in-flight check for this field
    controllersRef.current[fieldName]?.abort();
    const controller = new AbortController();
    controllersRef.current[fieldName] = controller;

    // Immediately mark all as "checking"
    setValidityMap((prev) => ({
      ...prev,
      [fieldName]: Object.fromEntries(ids.map((id) => [id, "checking"])),
    }));

    try {
      // ONE batched request for all IDs in this field
      const url = `${EUTILS_BASE}?db=pubmed&retmode=json&id=${ids.join(",")}`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error("API error");

      const data = await res.json();
      const result = data?.result || {};

      setValidityMap((prev) => ({
        ...prev,
        [fieldName]: Object.fromEntries(
          ids.map((id) => {
            const record = result[String(id)];
            // Invalid PMIDs: no record, has error field, or uid is missing/empty
            const isValid =
              record &&
              !record.error &&
              record.uid &&
              record.uid !== "" &&
              record.title !== undefined;
            return [id, isValid ? "valid" : "invalid"];
          })
        ),
      }));

    } catch (err) {
      if (err.name === "AbortError") return;
      // Real network failure
      setValidityMap((prev) => ({
        ...prev,
        [fieldName]: Object.fromEntries(ids.map((id) => [id, "unknown"])),
      }));
    }
  }, []);

  const resetField = useCallback((fieldName) => {
    controllersRef.current[fieldName]?.abort();
    setValidityMap((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    Object.values(controllersRef.current).forEach((c) => c.abort());
    controllersRef.current = {};
    setValidityMap({});
  }, []);

  return { validityMap, validateIds, resetField, resetAll };
}